import { useEffect, useState } from "react";
import { Trash2, ArrowUp, ArrowDown, Save, BookOpen } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ImageDropZone from "./ImageDropZone";
import { compressToWebP, withUploadTimeout } from "@/lib/imageCompression";

interface Article {
  id: string;
  slot: number;
  key: string;
  slug: string;
  title: string;
  intro: string;
  body: string;
  duration: string;
  price: string;
}

interface ArticleImage {
  id: string;
  article_id: string;
  slot: number;
  image_url: string;
  alt_text: string;
}

const ArticleEditor = ({ article, onChanged }: { article: Article; onChanged: () => void }) => {
  const [form, setForm] = useState(article);
  const [images, setImages] = useState<ArticleImage[]>([]);
  const [saving, setSaving] = useState(false);

  const loadImages = async () => {
    const { data } = await (supabase as any)
      .from("guide_article_images")
      .select("*")
      .eq("article_id", article.id)
      .order("slot", { ascending: true });
    setImages(data || []);
  };

  useEffect(() => { loadImages(); }, [article.id]);

  const save = async () => {
    const cleanSlug = (form.slug || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
    if (!cleanSlug) { toast.error("Le slug ne peut pas être vide"); return; }
    setSaving(true);
    const { error } = await (supabase as any)
      .from("guide_articles")
      .update({
        title: form.title,
        slug: cleanSlug,
        intro: form.intro,
        body: form.body,
        duration: form.duration,
        price: form.price,
      })
      .eq("id", article.id);
    setSaving(false);
    if (error) {
      const msg = /duplicate|unique/i.test(error.message)
        ? "Ce slug est déjà utilisé par un autre article."
        : error.message;
      toast.error(msg);
    } else {
      setForm({ ...form, slug: cleanSlug });
      toast.success("Article enregistré");
      onChanged();
    }
  };

  const upload = async (file: File): Promise<void> => {
    const webp = await compressToWebP(file, { maxSizeMB: 0.4 });
    const path = `guide-articles/${article.key}/img-${Date.now()}.webp`;
    const { error: upErr } = await withUploadTimeout(
      supabase.storage.from("site-images").upload(path, webp, { contentType: webp.type, upsert: false })
    );
    if (upErr) throw new Error(upErr.message);
    const { data: urlData } = supabase.storage.from("site-images").getPublicUrl(path);
    const maxSlot = images.length ? Math.max(...images.map((i) => i.slot)) : 0;
    const { error } = await (supabase as any).from("guide_article_images").insert({
      article_id: article.id,
      slot: maxSlot + 1,
      image_url: `${urlData.publicUrl}?t=${Date.now()}`,
      alt_text: article.title,
    });
    if (error) throw new Error(error.message);
    toast.success("Image ajoutée");
    loadImages();
  };

  const updateAlt = async (id: string, alt_text: string) => {
    const { error } = await (supabase as any).from("guide_article_images").update({ alt_text }).eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Alt mis à jour"); loadImages(); }
  };

  const remove = async (id: string) => {
    const { error } = await (supabase as any).from("guide_article_images").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Image supprimée"); loadImages(); }
  };

  const move = async (img: ArticleImage, dir: -1 | 1) => {
    const idx = images.findIndex((i) => i.id === img.id);
    const swap = images[idx + dir];
    if (!swap) return;
    await (supabase as any).from("guide_article_images").update({ slot: swap.slot }).eq("id", img.id);
    await (supabase as any).from("guide_article_images").update({ slot: img.slot }).eq("id", swap.id);
    loadImages();
  };

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          Article : {form.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs text-muted-foreground">Titre</label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Durée</label>
              <Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Prix</label>
              <Input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Slug (URL)</label>
          <Input
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            placeholder="ex: visite-essaouira"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Sera utilisé dans l'URL : <code className="font-mono">/guide/{form.slug || "votre-slug"}</code>
            {" — "}lettres minuscules, chiffres et tirets uniquement.
          </p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Intro (texte court visible avant "Lire la suite")</label>
          <Textarea
            rows={3}
            value={form.intro}
            onChange={(e) => setForm({ ...form, intro: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Description complète (affichée après "Lire la suite")</label>
          <Textarea
            rows={6}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
          />
        </div>
        <Button onClick={save} disabled={saving} className="bg-gradient-gold text-primary-foreground">
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Enregistrement…" : "Enregistrer l'article"}
        </Button>

        <div className="pt-4 border-t border-border">
          <p className="mb-3 text-sm font-medium">Images du carousel ({images.length})</p>
          <ImageDropZone
            onUpload={upload}
            accept="image/*"
            label="Glissez une image (JPG/PNG/WEBP) — compression WebP automatique"
            height="h-24"
          />
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {images.map((img, i) => (
              <div key={img.id} className="rounded-md border border-border p-2 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Slot {img.slot}</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" disabled={i === 0} onClick={() => move(img, -1)}>
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" disabled={i === images.length - 1} onClick={() => move(img, 1)}>
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(img.id)} className="text-destructive hover:text-destructive/80">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <img src={img.image_url} alt={img.alt_text} className="h-28 w-full rounded bg-secondary object-cover" loading="lazy" />
                <Input
                  defaultValue={img.alt_text}
                  placeholder="Texte alternatif SEO"
                  onBlur={(e) => { if (e.target.value !== img.alt_text) updateAlt(img.id, e.target.value); }}
                />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminGuideArticles = () => {
  const [articles, setArticles] = useState<Article[]>([]);

  const load = async () => {
    const { data } = await (supabase as any)
      .from("guide_articles")
      .select("*")
      .order("slot", { ascending: true });
    setArticles(data || []);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <p className="font-body text-sm text-muted-foreground">
        Gérez les 5 articles du Guide Essaouira : modifiez les textes, le prix, la durée et le carousel d'images de chaque article.
      </p>
      {articles.map((a) => (
        <ArticleEditor key={a.id} article={a} onChanged={load} />
      ))}
    </div>
  );
};

export default AdminGuideArticles;